import type { LatLon } from "@radar-sim/schema";
import {
  bearingDeg,
  distanceNm,
  EARTH_RADIUS_NM,
  normaliseBearing,
} from "@radar-sim/engine";

/** Operating orientation for the PPI. */
export type PPIMode = "head-up" | "north-up" | "course-up";

export interface PPIViewport {
  /** Centre x in canvas pixels (own-ship). */
  cx: number;
  /** Centre y in canvas pixels (own-ship). */
  cy: number;
  /** Pixel radius of the PPI circle. */
  radiusPx: number;
  /** Range scale in nautical miles (matches the outer ring). */
  rangeNm: number;
  /** Display orientation. */
  mode: PPIMode;
  /** Own-ship course over ground in degrees true. */
  ownCog: number;
  /** Optional course-up reference (north + this offset = up). */
  courseUpReferenceDeg?: number;
}

export interface ScreenXY {
  x: number;
  y: number;
}

/**
 * Up-direction in display terms. Returns the bearing (degrees true) that
 * corresponds to "up" on the screen for the configured mode.
 */
export function upBearingDeg(viewport: PPIViewport): number {
  switch (viewport.mode) {
    case "head-up":
      return viewport.ownCog;
    case "north-up":
      return 0;
    case "course-up":
      return viewport.courseUpReferenceDeg ?? viewport.ownCog;
  }
}

/**
 * Convert a true bearing + range from own ship into canvas screen XY.
 * Pure function: caller passes everything explicitly.
 */
export function bearingRangeToScreen(
  trueBearing: number,
  rangeNm: number,
  viewport: PPIViewport,
): ScreenXY {
  const up = upBearingDeg(viewport);
  const screenBearing = normaliseBearing(trueBearing - up);
  const r = (rangeNm / viewport.rangeNm) * viewport.radiusPx;
  const rad = (screenBearing * Math.PI) / 180;
  return {
    x: viewport.cx + r * Math.sin(rad),
    y: viewport.cy - r * Math.cos(rad),
  };
}

export function latLonToScreen(
  ownPosition: LatLon,
  target: LatLon,
  viewport: PPIViewport,
): ScreenXY {
  const range = distanceNm(ownPosition, target);
  if (range === 0) return { x: viewport.cx, y: viewport.cy };
  const brg = bearingDeg(ownPosition, target);
  return bearingRangeToScreen(brg, range, viewport);
}

/**
 * Inverse: convert a screen-space XY to a (trueBearing, rangeNm) pair.
 * Used for EBL/VRM drag interactions and tap-to-acquire MARPA.
 */
export function screenToBearingRange(
  xy: ScreenXY,
  viewport: PPIViewport,
): { trueBearing: number; rangeNm: number } {
  const dx = xy.x - viewport.cx;
  const dy = xy.y - viewport.cy;
  const distPx = Math.sqrt(dx * dx + dy * dy);
  const rangeNm = (distPx / viewport.radiusPx) * viewport.rangeNm;
  const screenBearing = normaliseBearing(
    (Math.atan2(dx, -dy) * 180) / Math.PI,
  );
  const up = upBearingDeg(viewport);
  return { trueBearing: normaliseBearing(screenBearing + up), rangeNm };
}

/**
 * Approximate small-area projection used by clutter/rain rendering: the
 * standard great-circle math is overkill for noise patterns scaled in
 * nautical miles. ~50 nm gives sub-pixel error.
 */
export function flatEarthOffsetNm(
  origin: LatLon,
  target: LatLon,
): { eastNm: number; northNm: number } {
  const dLat = (target.lat - origin.lat) * (Math.PI / 180);
  const dLon = (target.lon - origin.lon) * (Math.PI / 180);
  const meanLat = ((origin.lat + target.lat) / 2) * (Math.PI / 180);
  return {
    eastNm: dLon * Math.cos(meanLat) * EARTH_RADIUS_NM,
    northNm: dLat * EARTH_RADIUS_NM,
  };
}

/** Standard radar range scales, in nautical miles. */
export const RANGE_SCALES_NM: ReadonlyArray<number> = [
  0.125,
  0.25,
  0.5,
  0.75,
  1.5,
  3,
  6,
  12,
  24,
  48,
  72,
];
